"use client";

import { clsx } from "clsx";
import posthog from "posthog-js";

import { FacebookIcon, InstagramIcon } from "@/components/ui/icons/Social";
import { SOCIAL_LINKS } from "./socialProfiles";

const ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
} as const;

// Facebook's mark is a flat brand color applied via `text-*` (see Social.tsx);
// Instagram's mark carries its own gradient fill and needs no color class.
const ICON_COLOR_CLASSES: Record<keyof typeof ICONS, string> = {
  facebook: "text-brand-facebook",
  instagram: "",
};

// Where SocialLinks is mounted, passed through into the social_link_clicked
// PostHog event — mirrors the TurnstileSurface pattern in
// lib/turnstile/useTurnstileToken.tsx so the event stays accurate if this
// component is ever reused beyond the homepage.
export type SocialSurface = "homepage";

type SocialLinksProps = {
  surface: SocialSurface;
  className?: string;
};

export function SocialLinks({ surface, className }: SocialLinksProps) {
  return (
    <div className={clsx("flex items-center gap-1", className)}>
      {SOCIAL_LINKS.map(({ platform, label, href }) => {
        const Icon = ICONS[platform];
        return (
          <a
            key={platform}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            onClick={() => posthog.capture("social_link_clicked", { platform, surface })}
            // p-1.5 around a 20px icon gives a 32px tap target — the icon's
            // own visual size stays untouched, matching the gap-1 wrapper
            // spacing to the pre-tap-target-fix ~12px gap between glyphs.
            // Brand colors stay constant on hover (esp. Instagram's gradient
            // fill, which a `text-*` hover swap couldn't touch anyway) — a
            // slight opacity/scale lift gives feedback instead. The scale
            // lift is motion, so it backs off under prefers-reduced-motion;
            // the opacity lift is not motion and stays.
            className={clsx(
              "inline-flex items-center justify-center p-1.5 rounded-[var(--radius-sm)] transition-[opacity,transform] hover:opacity-80 hover:scale-110 motion-reduce:hover:scale-100 focus-ring",
              ICON_COLOR_CLASSES[platform],
            )}
          >
            <Icon size={20} />
          </a>
        );
      })}
    </div>
  );
}
